/**
 * CustomFieldManager Component
 *
 * Handles all custom field-related functionality for additional questions:
 * - Custom field selection and mapping
 * - Custom field deletion
 * - Custom field modal management
 * - Custom field visualization for supported field types
 */

import { Button } from '@HoudiniUI/Button';
import { Pill } from '@HoudiniUI/Pill/Pill';
import { css } from 'aphrodite';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { XIcon } from '../../../../../../components/svgs/icons/XIcon';
import { charmGray } from '../../../../../../styles/colors';
import { CustomFieldModal } from '../CustomFieldModal';
import { styleSheet } from '../styles';
import { CustomFieldManagerProps, CustomFieldModalState } from '../types';

/**
 * CustomFieldManager component for handling custom field functionality
 */
export const CustomFieldManager: React.FC<CustomFieldManagerProps> = ({
	fieldId,
	fieldLabel,
	isOptionField: _isOptionField,
	customFieldMap,
	onCustomFieldChange,
	onCustomFieldDelete,
	disabled = false,
	onModalCreateCustomField,
	onModalEditCustomField,
	availableCustomFields = [],
	usedCustomFieldIds = [],
	onValidationTrigger,
	questionIndex,
}) => {
	const formContext = useFormContext();
	const watch = formContext?.watch;
	const [customFieldModalState, setCustomFieldModalState] = React.useState<CustomFieldModalState>({
		isOpen: false,
		questionLabel: '',
		fieldId: '',
		isEdit: false,
		selectedCustomField: undefined,
	});

	// Helper functions for custom field management
	const hasCustomField = React.useCallback((): boolean => {
		return (
			customFieldMap?.some(mapping => {
				return mapping.fieldId === fieldId;
			}) || false
		);
	}, [fieldId, customFieldMap]);

	const getCustomField = React.useCallback((): { id: string; name: string; label: string } | null => {
		const mapping = customFieldMap?.find(m => {
			return m.fieldId === fieldId;
		});

		if (!mapping) return null;

		// Find the custom field details from availableCustomFields
		// Since custom fields have empty IDs, we need to match by label or name
		const customFieldData = availableCustomFields.find(
			field => field.name === mapping.contactFieldId || field.label === mapping.contactFieldId
		);

		if (!customFieldData) {
			return null;
		}

		return {
			id: customFieldData.id || customFieldData.name || mapping.contactFieldId,
			name: mapping.contactFieldId,
			label: customFieldData.label || mapping.contactFieldId,
		};
	}, [fieldId, customFieldMap, availableCustomFields]);

	// Modal management handlers
	const handleCustomFieldModalOpen = React.useCallback(
		async (questionLabel: string, isEditing: boolean) => {
			// If creating a new custom field mapping (not editing) and validation trigger is available, check if field is empty
			if (!isEditing && onValidationTrigger && watch) {
				// Get the current field value from the form
				const currentField = watch(`customForm.fields.${questionIndex}`);

				const isFieldEmpty = !currentField?.label?.trim();

				if (isFieldEmpty) {
					const isValid = await onValidationTrigger(questionIndex);
					// If validation fails, don't open the modal
					if (!isValid) {
						return;
					}
				}
			}

			const currentCustomField = isEditing ? getCustomField() : undefined;
			setCustomFieldModalState({
				isOpen: true,
				questionLabel,
				fieldId,
				isEdit: isEditing,
				selectedCustomField: currentCustomField || undefined,
			});
		},
		[fieldId, getCustomField, onValidationTrigger, watch, questionIndex]
	);

	const handleCustomFieldModalClose = React.useCallback(
		(result?: { fieldId: string; customFieldId: string; customFieldName: string } | null, canceled?: boolean) => {
			if (!canceled && result) {
				const { customFieldId, customFieldName } = result;
				onCustomFieldChange(fieldId, customFieldId, customFieldName);
			}
			setCustomFieldModalState(prev => ({ ...prev, isOpen: false, questionLabel: '', selectedCustomField: undefined }));
		},
		[fieldId, onCustomFieldChange]
	);

	const handleDeleteCustomField = React.useCallback(() => {
		onCustomFieldDelete(fieldId);
		setCustomFieldModalState(prev => ({ ...prev, isOpen: false, questionLabel: '', selectedCustomField: undefined }));
	}, [fieldId, onCustomFieldDelete]);

	const fieldHasCustomField = hasCustomField();
	const customField = getCustomField();

	// Expose modal handlers to parent component - ALWAYS set these up, even if we don't render UI
	React.useEffect(() => {
		if (onModalCreateCustomField) {
			onModalCreateCustomField(() => {
				// Use a fallback label if fieldLabel is empty
				const questionLabel = fieldLabel || `Question ${fieldId.slice(-4)}`;
				handleCustomFieldModalOpen(questionLabel, false);
			});
		}
	}, [onModalCreateCustomField, handleCustomFieldModalOpen, fieldLabel, fieldId]);

	React.useEffect(() => {
		if (onModalEditCustomField) {
			onModalEditCustomField(() => handleCustomFieldModalOpen(customField?.label || fieldLabel, true));
		}
	}, [onModalEditCustomField, handleCustomFieldModalOpen, fieldLabel, customField?.label]);

	// Don't render anything if disabled
	if (disabled) {
		return null;
	}

	// If no existing custom field and no field label, render nothing (unless hideUI is true)
	if (!fieldHasCustomField && !fieldLabel) {
		return null;
	}

	return (
		<>
			{/* Custom Field Management UI */}
			{fieldHasCustomField && customField ? (
				// Edit existing custom field
				<div className={css(styleSheet.tagPrefixContainer)}>
					<p>Responses linked to custom field: </p>
					<Pill label={customField.label} variant='label' color='teal' />{' '}
					<Button
						type='button'
						styles={[styleSheet.removeOptionButton]}
						onClick={handleDeleteCustomField}
						data-testid='remove-option'
					>
						<XIcon color={charmGray} width={16} height={16} />
					</Button>
				</div>
			) : null}

			{/* Custom Field Modal */}
			<CustomFieldModal
				isOpen={customFieldModalState.isOpen}
				onRequestClose={handleCustomFieldModalClose}
				questionLabel={customFieldModalState.questionLabel}
				fieldId={customFieldModalState.fieldId}
				hasExistingMapping={customFieldModalState.isEdit}
				availableCustomFields={availableCustomFields}
				usedCustomFieldIds={usedCustomFieldIds}
				selectedCustomField={customFieldModalState.selectedCustomField}
			/>
		</>
	);
};
